import { useCallback, useEffect, useMemo, useState } from "react";
import { faBolt, faChartLine, faCirclePlus, faGauge, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocalStorage } from "usehooks-ts";

import { Colors } from "design/colors";
import { Gauge, IGaugeProps } from "./Gauge";
import { FaultControl, IFaultControlProps } from "./FaultControl";
import { Throttle } from "./Throttle";
import { IPlotProps, Plot } from "./Plot";
import { MessageKeySelect } from "../MessageKeySelect";
import { InputMode, ControlMode } from "utils/common-types";
import { PlaybackButton } from "../navbar/PlaybackButton";
import { PlaybackSlider } from "../navbar/PlaybackSlider";
import { useDevices } from "hooks/useDevices";
import { ExportButton } from "../navbar/ExportButton";
import { SortableWidgetsContext, SortableWidgetsItem } from "./sortable";
import { Position } from "./Position";
import { hidSend } from "connections/hid";
import { serializeClearAllFaultsRequest } from "utils/common-types";
import { multiMotorNodeId } from "utils/packet-workers";
import { useTelemSet } from "hooks/useTelemSet";
import { PlotKey } from "utils/packet.worker";
import { telem } from "cstore/toml/toml";
import { TelemToml } from "cstore/telem";
import { useSubActuators } from "hooks/useSubActuators";
import { SubActuators } from "utils/slnt-file";

enum WidgetType {
    Plot,
    Gauge,
    Throttle,
    FaultControl,
}

export interface ICommonWidgetProps {
    close: () => void;
}

interface IWidgetProps {
    [ WidgetType.Plot ]: IPlotProps;
    [ WidgetType.Gauge ]: IGaugeProps;
    [ WidgetType.Throttle ]: undefined;
    [ WidgetType.FaultControl ]: IFaultControlProps;
}

interface IWidget<T extends WidgetType> {
    widgetType: T;
    widgetProps: IWidgetProps[ T ];
}

export type Widget = IWidget<WidgetType>;

const widgetElement = (
    _widget: Widget,
    controlMode: ControlMode | undefined,
    close: () => void,
    addKey: ( packetKey: PlotKey ) => void,
    removeKey: ( packetKey: PlotKey ) => void,
    clearAllFaults: () => void,
): JSX.Element => {
    switch ( _widget.widgetType ) {
        case WidgetType.Plot: {
            const widget = _widget as IWidget<WidgetType.Plot>;
            return <Plot { ...widget.widgetProps } addKey={ addKey } removeKey={ removeKey } />;
        }
        case WidgetType.Gauge: {
            const widget = _widget as IWidget<WidgetType.Gauge>;
            return <Gauge { ...widget.widgetProps } close={ close } />;
        }
        case WidgetType.Throttle: {
            if ( controlMode === ControlMode.POSITION) {
                return <Position close={ close } />
            }
            return <Throttle close={ close } />;
        }
        case WidgetType.FaultControl: {
            const widget = _widget as IWidget<WidgetType.FaultControl>;
            return <FaultControl { ...widget.widgetProps } close={ close } clearAllFaults={ clearAllFaults } />;
        }
    }
};

const widget = <T extends WidgetType>( widgetType: T, widgetProps: IWidgetProps[ T ] ): IWidget<T> => {
    return {
        widgetType,
        widgetProps,
    };
};

// Do not allow the user to select these
const alwaysExcludePlotKeys: ( PlotKey | RegExp )[] = [
    "controllerState",
    "faultCodeDeprecated",
    "faultsBitmapLower32",
    "faultsBitmapUpper16",
    /^MultiMotor_N\d+_faultsBitmapLower32$/,
    /^MultiMotor_N\d+_faultsBitmapUpper16$/,
];

// Reusable telemetry submenu component - GAUGE and CHART have identical structure
const TelemetrySubmenu = ( props: {
    addWidget: ( widget: Widget ) => void;
    subActuators: SubActuators;
    label: string;
    icon: typeof faGauge;
    widgetType: WidgetType.Gauge | WidgetType.Plot;
    isOpen: boolean;
    setIsOpen: ( open: boolean ) => void;
    popoverIsOpen: boolean;
    setPopoverIsOpen: ( open: boolean ) => void;
} ) => {
    const { label, icon, widgetType, isOpen, setIsOpen, setPopoverIsOpen, addWidget, subActuators } = props;
    const isGauge = widgetType === WidgetType.Gauge;

    return (
        <div
            className="hover-fade"
            style={{ display: "flex", alignItems: "center", position: "relative" }}
            onMouseOver={ () => setIsOpen( true ) }
            onMouseLeave={ () => setIsOpen( false ) }
        >
            <span style={{ color: Colors.WHITE, marginRight: 4 }}>{ label }</span>
            <FontAwesomeIcon icon={ icon } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
            { isOpen && (
                <div style={{
                    position: "absolute",
                    right: "100%",
                    bottom: 0,
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                }}>
                    <MessageKeySelect
                        nodeId="main"
                        onSelect={ key => addWidget( widget( widgetType, isGauge ? { packetKey: key } : { packetKeys: [ key ] } ) ) }
                        exclude={ alwaysExcludePlotKeys }
                        onOpening={ () => setPopoverIsOpen( true ) }
                        onClose={ () => setPopoverIsOpen( false ) }
                    >
                        <div className="hover-fade" style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ color: Colors.WHITE, marginRight: 4 }}>MAIN BOARD</span>
                            <FontAwesomeIcon icon={ icon } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
                        </div>
                    </MessageKeySelect>
                    { Object.keys( subActuators ).map( Number ).map( nodeId => (
                        <MessageKeySelect
                            key={ nodeId }
                            nodeId={ nodeId }
                            onSelect={ key => addWidget( widget( widgetType, isGauge ? { packetKey: key } : { packetKeys: [ key ] } ) ) }
                            exclude={ alwaysExcludePlotKeys }
                            onOpening={ () => setPopoverIsOpen( true ) }
                            onClose={ () => setPopoverIsOpen( false ) }
                        >
                            <div className="hover-fade" style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ color: Colors.WHITE, marginRight: 4 }}>
                                    { subActuators[ nodeId ].disposition.toUpperCase() }
                                </span>
                                <FontAwesomeIcon icon={ icon } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
                            </div>
                        </MessageKeySelect>
                    ) ) }
                </div>
            ) }
        </div>
    );
};


const AddWidgetButton = ( props: {
    addWidget: ( widget: Widget ) => void;
    permitAddThrottle: boolean;
    subActuators: SubActuators;
    close: () => void;
} ) => {
    const [ gaugePopoverIsOpen, setGaugePopoverIsOpen ] = useState<boolean>( false );
    const [ plotPopoverIsOpen, setPlotPopoverIsOpen ] = useState<boolean>( false );
    const [ faultsSubmenuOpen, setFaultsSubmenuOpen ] = useState<boolean>( false );
    const [ gaugeSubmenuOpen, setGaugeSubmenuOpen ] = useState<boolean>( false );
    const [ chartSubmenuOpen, setChartSubmenuOpen ] = useState<boolean>( false );
    const [ isHovering, setIsHovering ] = useState<boolean>( false );

    const { addWidget, permitAddThrottle, subActuators } = props;

    let icons = null;
    const hasSubActuators = Object.keys( subActuators ).length > 0;

    if ( isHovering || gaugePopoverIsOpen || plotPopoverIsOpen || faultsSubmenuOpen || gaugeSubmenuOpen || chartSubmenuOpen ) {
        icons = (
            <>
                {/* Fault controls */}
                <div
                    className="hover-fade"
                    style={{ display: "flex", alignItems: "center", position: "relative" }}
                    onMouseOver={ () => setFaultsSubmenuOpen( true ) }
                    onMouseLeave={ () => setFaultsSubmenuOpen( false ) }
                >
                    <span style={{ color: Colors.WHITE, marginRight: 4 }}>FAULTS</span>
                    <FontAwesomeIcon icon={ faTriangleExclamation } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
                    { faultsSubmenuOpen && (
                        <div style={{
                            position: "absolute",
                            right: "100%",
                            zIndex: 10,
                            bottom: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                        }}>
                            <div className="hover-fade" style={{ display: "flex", alignItems: "center" }}
                                onClick={ () => addWidget( widget( WidgetType.FaultControl, faultControlProps() ) ) }
                            >
                                <span style={{ color: Colors.WHITE, marginRight: 4 }}>MAIN BOARD</span>
                                <FontAwesomeIcon icon={ faTriangleExclamation } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
                            </div>
                            { Object.keys( subActuators ).map( Number ).map( nodeId => (
                                <div key={ nodeId } className="hover-fade" style={{ display: "flex", alignItems: "center" }}
                                    onClick={ () => addWidget( widget( WidgetType.FaultControl, multiMotorFaultControlProps( nodeId ) ) ) }
                                >
                                    <span style={{ color: Colors.WHITE, marginRight: 4 }}>
                                        { subActuators[ nodeId ].disposition.toUpperCase() }
                                    </span>
                                    <FontAwesomeIcon icon={ faTriangleExclamation } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
                                </div>
                            ) ) }
                        </div>
                    ) }
                </div>
                {/* Throttle */}
                { permitAddThrottle &&
                    <div className="hover-fade" style={{ display: "flex", alignItems: "center" }}
                        onClick={ () => addWidget( widget( WidgetType.Throttle, undefined ) ) }
                    >
                        <span style={{ color: Colors.WHITE, marginRight: 4 }}>THROTTLE</span>
                        <FontAwesomeIcon icon={ faBolt } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
                    </div>
                }
                {/* Gauge */}
                { hasSubActuators
                    ? <TelemetrySubmenu 
                        addWidget={ addWidget }
                        subActuators={ subActuators }
                        label="GAUGE" 
                        icon={ faGauge } 
                        widgetType={ WidgetType.Gauge }
                        isOpen={ gaugeSubmenuOpen }
                        setIsOpen={ setGaugeSubmenuOpen }
                        popoverIsOpen={ gaugePopoverIsOpen }
                        setPopoverIsOpen={ setGaugePopoverIsOpen }
                      />
                    : (
                    <MessageKeySelect
                        onSelect={ key => addWidget( widget( WidgetType.Gauge, { packetKey: key } ) ) }
                        exclude={ alwaysExcludePlotKeys }
                        onOpening={ () => setGaugePopoverIsOpen( true ) }
                        onClose={ () => setGaugePopoverIsOpen( false ) }
                    >
                        <div className="hover-fade" style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ color: Colors.WHITE, marginRight: 4 }}>GAUGE</span>
                            <FontAwesomeIcon icon={ faGauge } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
                        </div>
                    </MessageKeySelect>
                ) }
                {/* Chart */}
                { hasSubActuators
                    ? <TelemetrySubmenu 
                        addWidget={ addWidget }
                        subActuators={ subActuators }
                        label="CHART" 
                        icon={ faChartLine } 
                        widgetType={ WidgetType.Plot }
                        isOpen={ chartSubmenuOpen }
                        setIsOpen={ setChartSubmenuOpen }
                        popoverIsOpen={ plotPopoverIsOpen }
                        setPopoverIsOpen={ setPlotPopoverIsOpen }
                      />
                    : (
                    <MessageKeySelect
                        onSelect={ key => addWidget( widget( WidgetType.Plot, { packetKeys: [ key ] } ) ) }
                        exclude={ alwaysExcludePlotKeys }
                        onOpening={ () => setPlotPopoverIsOpen( true ) }
                        onClose={ () => setPlotPopoverIsOpen( false ) }
                    >
                        <div className="hover-fade" style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ color: Colors.WHITE, marginRight: 4 }}>CHART</span>
                            <FontAwesomeIcon icon={ faChartLine } size="4x" style={{ padding: 8 }} color={ Colors.WHITE } />
                        </div>
                    </MessageKeySelect>
                ) }
            </>
        );
    }

    return (
        <div
            className="hover-fade"
            style={{
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                alignItems: "flex-end",
            }}
            onMouseOver={ () => setIsHovering( true ) }
            onMouseLeave={ () => setIsHovering( false ) }
        >
            { icons }
            <FontAwesomeIcon
                icon={ faCirclePlus }
                size="4x"
                color={ Colors.WHITE }
                style={{ padding: 8 }}
            />
        </div>
    );
};

const MIN_CARD_WIDTH = 550;

const hasWidgetOfType = ( widgets: Widget[], widgetType: WidgetType ): boolean => widgets.findIndex( w => w.widgetType === widgetType ) !== -1;

const faultControlProps = (): IFaultControlProps => {
    return {
        faultBitmapLowerPacketKey: "faultsBitmapLower32",
        faultBitmapUpperPacketKey: "faultsBitmapUpper16",
    };
}

const multiMotorFaultControlProps = ( nodeId: number ): IFaultControlProps => ({
    faultBitmapLowerPacketKey: `MultiMotor_N${nodeId}_faultsBitmapLower32` as PlotKey,
    faultBitmapUpperPacketKey: `MultiMotor_N${nodeId}_faultsBitmapUpper16` as PlotKey,
    hideClearAllFaultsBtn: true, // Currently no way to clear all faults on sub-actuators
});

const defaultWidgets: Widget[] = [
    widget( WidgetType.Throttle, undefined ),
    widget( WidgetType.Plot, { packetKeys: [ "motorSpeed", "servoPulse" ] } ),
    widget( WidgetType.Plot, { packetKeys: [ "batteryVoltage" ] } ),
    widget( WidgetType.Plot, { packetKeys: [ "uptimeMs" ] } ),
    widget( WidgetType.Plot, { packetKeys: [ "motorCurrent", "batteryCurrent" ] } ),
    widget( WidgetType.Gauge, { packetKey: "inverterTemp" } ),
    widget( WidgetType.Gauge, { packetKey: "controllerState" } ),
    widget( WidgetType.FaultControl, faultControlProps() ),
    widget( WidgetType.FaultControl, multiMotorFaultControlProps( 1 ) ),
    widget( WidgetType.FaultControl, multiMotorFaultControlProps( 2 ) ),
    widget( WidgetType.FaultControl, multiMotorFaultControlProps( 3 ) ),
    widget( WidgetType.FaultControl, multiMotorFaultControlProps( 4 ) ),
];

const PANEL_PADDING = 16;

const gaugePacketKeysToRemove: string[] = [ "faultCodeDeprecated" ];

// This is necessary to handle renamed widgets not matching with local storage.
const renameWidgets = ( ws: Widget[] ): Widget[] => {
    let modified = false;
    let newWidgets = ws.filter( ( _widget ) => {
        switch ( _widget.widgetType ) {
            case WidgetType.Gauge: {
                const widget = _widget as IWidget<WidgetType.Gauge>;
                const packetKey = widget?.widgetProps?.packetKey as string;
                if ( gaugePacketKeysToRemove.includes( packetKey ) ) {
                    modified = true;
                    return false;
                }
                return true;
            }
            default:
                return true;
        }
    });
    if ( modified ) {
        ws = newWidgets;
    }
    newWidgets = ws.map( ( _widget ) => {
        switch ( _widget.widgetType ) {
            case WidgetType.Gauge: {
                const widget = _widget as IWidget<WidgetType.Gauge>;
                if ( widget?.widgetProps?.packetKey as string === "faultCode" ) {
                    modified = true;
                    // Change a Gauge into a FaultControl
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    let widgetProps = { ...widget.widgetProps, ...faultControlProps() } as any;
                    delete widgetProps.packetKey;
                    return { widgetType: WidgetType.FaultControl, widgetProps } as Widget;
                }
                return _widget;
            }
            default:
                return _widget;
        }
    });
    if ( modified ) {
        ws = newWidgets;
    }
    return ws;
};

const allVarTelemKeys: PlotKey[] = (telem as TelemToml).telemetry.definitions.map( (entry) => entry.name );

export const WidgetPanel = () => {
    const { connectedDevice } = useDevices();
    const [ containerWidth, setContainerWidth ] = useState<number>( 1 );
    const [ sliderHeight, setSliderHeight ] = useState<number>( 1 );
    const panelWidth = Math.max( containerWidth - PANEL_PADDING * 2, 1 );
    const enableThrottle = connectedDevice !== undefined && connectedDevice.initialized && connectedDevice.inputConfig.inputMode === InputMode.USB;
    const controlMode = enableThrottle ? connectedDevice.controlMode : undefined;

    const [ widgets, setWidgets ] = useLocalStorage<Widget[]>( "dash", defaultWidgets );

    const [ widgetWidth, widgetHeight ] = useMemo( () => {
        const numColumns = Math.floor( panelWidth / Math.min( MIN_CARD_WIDTH, panelWidth ) );
        const _widgetWidth = Math.floor( panelWidth / numColumns );
        const _widgetHeight = Math.floor( _widgetWidth / 1.5 );

        return [ _widgetWidth, _widgetHeight ]
    }, [ panelWidth ] );

    // This is necessary to handle renamed widgets not matching with local storage.
    useEffect(() => {
        let newWidgets = renameWidgets( widgets );
        if ( newWidgets !== widgets ) {
            setWidgets( newWidgets );
        }
    }, [ widgets, setWidgets ]);

    const addWidget = useCallback( ( widget: Widget ) => {
        setWidgets( ws => {
            if ( widget.widgetType === WidgetType.Throttle && hasWidgetOfType( ws, WidgetType.Throttle ) ) {
                return ws;
            }

            return [ ...ws, widget ];
        } );
    }, [ setWidgets ] );

    const removeWidget = useCallback( ( index: number ) => {
        setWidgets( ws => ws.slice( 0, index ).concat( ws.slice( index + 1 ) ) );
    }, [ setWidgets ] );

    const addKey = useCallback( ( index: number, key: PlotKey ) => {
        // TODO: verify `key` represents a valid numeric telemetry field name
        setWidgets( ws => {
            const _widget = ws[ index ];
            if ( _widget === undefined || _widget.widgetType !== WidgetType.Plot ) {
                return ws;
            }

            const widget = _widget as IWidget<WidgetType.Plot>;

            return [ ...ws.slice( 0, index ), { ...widget, widgetProps: { ...widget.widgetProps, packetKeys: [ ...widget.widgetProps.packetKeys, key ] } } as unknown as Widget, ...ws.slice( index + 1 ) ];
        } );
    }, [ setWidgets ] );

    const removeKey = useCallback( ( index: number, key: PlotKey ) => {
        setWidgets( ws => {
            const _widget = ws[ index ];
            if ( _widget === undefined || _widget.widgetType !== WidgetType.Plot ) {
                return ws;
            }

            const widget = _widget as IWidget<WidgetType.Plot>;

            const keyIndex = widget.widgetProps.packetKeys.indexOf( key );
            if ( keyIndex === -1 ) {
                return ws;
            }

            if ( widget.widgetProps.packetKeys.length === 1 ) {
                return ws.slice( 0, index ).concat( ws.slice( index + 1 ) );
            }

            return [
                ...ws.slice( 0, index ),
                { ...widget,
                    widgetProps: {
                        ...widget.widgetProps,
                        packetKeys: [ ...widget.widgetProps.packetKeys.slice( 0, keyIndex ), ...widget.widgetProps.packetKeys.slice( keyIndex + 1 ) ]
                    }
                } as unknown as Widget,
                ...ws.slice( index + 1 )
            ];
        } );
    }, [ setWidgets ] );

    const clearAllFaults = useCallback( () => {
        if ( connectedDevice !== undefined ) {
            hidSend( connectedDevice.handle, serializeClearAllFaultsRequest({}) );
        }
    }, [ connectedDevice ]);

    const subActuators = useSubActuators();

    const telemSet = useTelemSet();
    const excludedPlotKeys = useMemo(
        () => {
            const enabledKeys = telemSet.map( el => el.fieldName );
            return allVarTelemKeys.filter( key => !enabledKeys.includes( key ) ) as PlotKey[];
        },
        [ telemSet ],
    );

    const widgetCards = useMemo( () => {
        const cards: JSX.Element[] = [];
        const marginSize = 8;
        const adjustedWidgetWidth = widgetWidth - marginSize * 2; // subtract margin on both sides

        const cardStyle: React.CSSProperties = {
            display: "flex",
            borderRadius: 10,
            backgroundColor: Colors.DARK_GREEN,
            boxShadow: "1px 2px 9px #222222",
            flexDirection: "column",
            margin: `${marginSize}px`,
        };

        const connectedNodeIds = new Set( Object.keys( subActuators ).map( Number ) );

        for ( let [ i, widget ] of widgets.entries() ) {
            if ( widget.widgetType === WidgetType.Throttle && !enableThrottle ) {
                continue;
            }

            // Filter out any disabled expanded telemetry.
            let packetKeys: PlotKey[] = [];
            if ( widget.widgetType === WidgetType.Gauge ) {
                packetKeys = [ ( widget.widgetProps as IGaugeProps ).packetKey ];
            } else if ( widget.widgetType === WidgetType.Plot ) {
                packetKeys = ( widget.widgetProps as IPlotProps ).packetKeys;
            }
            if ( packetKeys.some( key => excludedPlotKeys.includes( key ) ) ) {
                continue;
            }

            // FaultControl: skip if multi-motor and actuator not connected; inject label
            if ( widget.widgetType === WidgetType.FaultControl ) {
                const fcProps = widget.widgetProps as IFaultControlProps;
                const nodeId = multiMotorNodeId( fcProps.faultBitmapLowerPacketKey );
                if ( nodeId !== undefined ) {
                    if ( !connectedNodeIds.has( nodeId ) ) continue;
                    const label = subActuators[ nodeId ]?.disposition;
                    widget = { ...widget, widgetProps: { ...fcProps, label } } as Widget;
                }
            }

            // Gauge: skip if multi-motor and actuator not connected
            if ( widget.widgetType === WidgetType.Gauge ) {
                const nodeId = multiMotorNodeId( ( widget.widgetProps as IGaugeProps ).packetKey );
                if ( nodeId !== undefined && !connectedNodeIds.has( nodeId ) ) continue;
            }

            // Plot: filter out disconnected multi-motor keys, keep non-multi-motor
            if ( widget.widgetType === WidgetType.Plot ) {
                const allKeys = ( widget.widgetProps as IPlotProps ).packetKeys;
                const visibleKeys = allKeys.filter( key => {
                    const nodeId = multiMotorNodeId( key );
                    // Keep if not telemetry from a sub-device OR it's a connected sub-device
                    return nodeId === undefined || connectedNodeIds.has( nodeId );
                } );
                if ( visibleKeys.length === 0 ) continue;
                if ( visibleKeys.length !== allKeys.length ) {
                    widget = { ...widget, widgetProps: { ...widget.widgetProps, packetKeys: visibleKeys } } as Widget;
                }
            }

            cards.push(
                <SortableWidgetsItem key={ i } id={ i.toString() } style={{ width: adjustedWidgetWidth, height: widgetHeight, padding: 16, ...cardStyle}}>
                    { widgetElement( widget, controlMode, removeWidget.bind( null, i ), addKey.bind( null, i ), removeKey.bind( null, i ), clearAllFaults ) }
                </SortableWidgetsItem>
            )
        }

        return cards;
    }, [ enableThrottle, widgets, widgetWidth, widgetHeight, removeWidget, addKey, removeKey, controlMode, clearAllFaults, excludedPlotKeys, subActuators ] );

    return (
        <div ref={ e => e === null ? undefined : setContainerWidth( e.clientWidth ) } style={{ display: "flex", padding: PANEL_PADDING, flex: 1 }}>
            <SortableWidgetsContext
                widgets={ widgets }
                setWidgets={ setWidgets }
            >
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        alignContent: "flex-start",
                        position: "relative",
                        paddingBottom: sliderHeight,
                    }}
                >
                    <div style={{ display: "flex", flexWrap: "wrap" }}>
                        { widgetCards }
                    </div>
                </div>
            </SortableWidgetsContext>
            <div style={{ position: "fixed", bottom: sliderHeight - 16, right: 16 }}>
                <AddWidgetButton
                    addWidget={ addWidget }
                    permitAddThrottle={ enableThrottle && !hasWidgetOfType( widgets, WidgetType.Throttle ) }
                    subActuators={ subActuators }
                    close={ removeWidget.bind( null, widgets.length ) } />
            </div>
            <div ref={ e => setSliderHeight( e?.clientHeight || 0 ) } style={{ position: "fixed", bottom: 0, right: 0, width: containerWidth }}>
                <div style={{ padding: 32 }}>
                    <div style={{ display: "flex", padding: 16, borderRadius: 8, backgroundColor: Colors.DARK_GREEN, boxShadow: "1px 2px 9px #222222" }}>
                        <div style={{ marginRight: 16, display: "flex", alignItems: "center" }}>
                            <ExportButton />
                            <div style={{ width: 8 }} />
                            <PlaybackButton />
                        </div>
                        <PlaybackSlider />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IWidget;
